import axios from 'axios'
import { useEffect, useState } from 'react'
import { APP_CONSTANTS, HERO_CARD } from '../constants'
import { getCourseCardDetails } from './courseCardHelper'
import { getTilesData } from './tilesDataHelper'
import {
  apiURL,
  commonHeadersWeb,
  getCountryCode,
  getMainLanguageCode,
  platformWeb,
} from '../services'
import { preloadImage } from 'src/utils/imagePreloader'
import {
  getWellnessTileId,
  getWellnessTileLabel,
  getWellnessTilePolicyId,
} from './wellnessTileTarget'

const CACHE_TTL_MS = 5 * 60 * 1000
const MAX_CACHE_ENTRIES = 12
const NAV_PREFETCH_LIMIT = 4
const NON_PREFETCHABLE_TILE_IDS = new Set(['explore-wellness-app'])

const wellnessTileDataCache = new Map()
const inflightTileRequests = new Map()
let cacheAuthToken = null

function getStorageItem(storage, key) {
  if (typeof storage === 'undefined') return null
  try {
    return storage.getItem(key)
  } catch {
    return null
  }
}

function removeStorageItem(storage, key) {
  if (typeof storage === 'undefined') return
  try {
    storage.removeItem(key)
  } catch {
    // ignore storage failures
  }
}

function getAccessToken() {
  return getStorageItem(localStorage, 'token') || ''
}

function syncAuthScopedCache(token) {
  if (cacheAuthToken === token) return
  wellnessTileDataCache.clear()
  inflightTileRequests.clear()
  cacheAuthToken = token
}

function pruneCache() {
  if (wellnessTileDataCache.size <= MAX_CACHE_ENTRIES) return

  const oldestKey = wellnessTileDataCache.keys().next().value
  if (oldestKey) {
    wellnessTileDataCache.delete(oldestKey)
  }
}

function buildMediaUrl(imageId) {
  if (!imageId) return null
  if (/^https?:\/\//i.test(imageId)) return imageId
  return `${APP_CONSTANTS.MEDIA_URL}${imageId}`
}

function safePreloadImage(imageUrl, options) {
  if (!imageUrl || typeof document === 'undefined') return
  preloadImage(imageUrl, options)
}

function isCancelableError(error) {
  return (
    axios.isCancel?.(error) ||
    error?.name === 'CanceledError' ||
    error?.code === 'ERR_CANCELED'
  )
}

function getTileTargetKey(target) {
  return `${target?.tileId || ''}:${target?.policyId || ''}`
}

function isPrefetchableTarget(target) {
  return Boolean(target?.tileId && !NON_PREFETCHABLE_TILE_IDS.has(target.tileId))
}

export function getTimezoneOffsetISO() {
  const offsetMinutes = new Date().getTimezoneOffset()
  const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60)
  const offsetMins = Math.abs(offsetMinutes) % 60
  const sign = offsetMinutes <= 0 ? '+' : '-'

  return `${sign}${String(offsetHours).padStart(2, '0')}:${String(offsetMins).padStart(2, '0')}`
}

export function getStoredTileGeoLocation() {
  const fallback = { lat: 0, lng: 0 }
  const geoLocationString = getStorageItem(localStorage, 'geoLocation')

  if (!geoLocationString || geoLocationString === 'undefined') {
    return fallback
  }

  try {
    const geoLocation = JSON.parse(geoLocationString)
    return {
      lat: geoLocation?.lat || 0,
      lng: geoLocation?.lng || 0,
    }
  } catch {
    removeStorageItem(localStorage, 'geoLocation')
    return fallback
  }
}

export function normalizeWellnessPrefetchTarget(target, policyId = '') {
  if (typeof target === 'string') {
    return { tileId: target, policyId: policyId || '', label: target }
  }

  const tileId = getWellnessTileId(target)
  if (!tileId) return null

  return {
    tileId,
    policyId: getWellnessTilePolicyId(target) || policyId || '',
    label: getWellnessTileLabel(target, tileId),
  }
}

export function buildWellnessTileRequestDescriptor(target, policyId = '') {
  const normalizedTarget = normalizeWellnessPrefetchTarget(target, policyId)
  if (!normalizedTarget) return null

  const token = getAccessToken()
  syncAuthScopedCache(token)

  const mainLanguageCode = getMainLanguageCode()
  const effectiveCountryCode = (getCountryCode() || 'in').toLowerCase()
  const { lat, lng } = getStoredTileGeoLocation()
  const zoneOffset = getTimezoneOffsetISO()
  const params = new URLSearchParams({
    languageCode: mainLanguageCode,
    platform: platformWeb,
    countryCode: effectiveCountryCode,
    tileType: normalizedTarget.tileId,
    policyId: normalizedTarget.policyId || '',
    zoneOffset,
    lat: String(lat),
    lng: String(lng),
  })

  return {
    ...normalizedTarget,
    key: [
      'wellness-tile',
      normalizedTarget.tileId,
      normalizedTarget.policyId || '',
      mainLanguageCode,
      effectiveCountryCode,
      zoneOffset,
      lat,
      lng,
    ].join('|'),
    url: `${apiURL}/api/secure/tile?${params.toString()}`,
    headers: {
      ...(token && { Authorization: `Bearer ${token}` }),
      ...commonHeadersWeb,
      'ef-user-country-code': effectiveCountryCode,
      'ef-app-language': mainLanguageCode,
    },
    token,
  }
}

export function getCachedWellnessTileData(target, policyId = '') {
  const descriptor = buildWellnessTileRequestDescriptor(target, policyId)
  if (!descriptor) return null

  const cachedEntry = wellnessTileDataCache.get(descriptor.key)
  if (!cachedEntry) return null

  if (Date.now() - cachedEntry.timestamp > CACHE_TTL_MS) {
    wellnessTileDataCache.delete(descriptor.key)
    return null
  }

  wellnessTileDataCache.delete(descriptor.key)
  wellnessTileDataCache.set(descriptor.key, cachedEntry)
  return cachedEntry.data
}

function setCachedWellnessTileData(descriptor, data) {
  if (!descriptor?.key || !data) return

  wellnessTileDataCache.set(descriptor.key, {
    data,
    timestamp: Date.now(),
  })
  pruneCache()
}

export async function fetchWellnessTileData(target, options = {}) {
  const descriptor = buildWellnessTileRequestDescriptor(target)
  if (!descriptor) {
    throw new Error('Missing wellness tile target')
  }

  const cachedData = getCachedWellnessTileData(descriptor)
  if (cachedData) return cachedData

  const inflightRequest = inflightTileRequests.get(descriptor.key)
  if (inflightRequest) return inflightRequest

  const request = axios
    .get(descriptor.url, {
      headers: descriptor.headers,
      signal: options.signal,
    })
    .then((res) => {
      setCachedWellnessTileData(descriptor, res.data)
      return res.data
    })
    .finally(() => {
      inflightTileRequests.delete(descriptor.key)
    })

  inflightTileRequests.set(descriptor.key, request)
  return request
}

export function prefetchWellnessTile(target, options = {}) {
  const normalizedTarget = normalizeWellnessPrefetchTarget(target)
  if (!isPrefetchableTarget(normalizedTarget) || !getAccessToken()) {
    return Promise.resolve(null)
  }

  return fetchWellnessTileData(normalizedTarget, options)
    .then((data) => {
      if (options.warmAssets !== false) {
        warmWellnessTileAssets(data, {
          maxImages: options.maxImages || 3,
          priority: 'low',
        })
      }
      return data
    })
    .catch((error) => {
      if (!isCancelableError(error) && options.logErrors) {
        console.warn('Wellness tile prefetch failed', error)
      }
      return null
    })
}

export function scheduleWellnessTilePrefetch(target, options = {}) {
  if (typeof window === 'undefined') return () => {}

  const normalizedTarget = normalizeWellnessPrefetchTarget(target)
  if (!isPrefetchableTarget(normalizedTarget)) return () => {}

  let timeoutId
  let idleId
  let canceled = false
  const delay = Math.max(options.delay || 0, 0)

  const runPrefetch = () => {
    if (canceled) return

    const idleCallback = () => {
      if (!canceled) {
        prefetchWellnessTile(normalizedTarget, options)
      }
    }

    if (typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(idleCallback, {
        timeout: options.timeout || 3500,
      })
    } else {
      idleId = window.setTimeout(idleCallback, 1)
    }
  }

  timeoutId = window.setTimeout(runPrefetch, delay)

  return () => {
    canceled = true
    window.clearTimeout(timeoutId)
    if (idleId && typeof window.cancelIdleCallback === 'function') {
      window.cancelIdleCallback(idleId)
    } else if (idleId) {
      window.clearTimeout(idleId)
    }
  }
}

export function getWellnessNavPrefetchCandidates(options = [], activeValue, limit = NAV_PREFETCH_LIMIT) {
  const normalizedOptions = options
    .map((option) => normalizeWellnessPrefetchTarget(option))
    .filter(isPrefetchableTarget)
  const activeIndex = normalizedOptions.findIndex((option) => option.tileId === activeValue)
  const orderedTargets = []
  const addTarget = (target) => {
    if (!target || target.tileId === activeValue) return
    if (orderedTargets.some((item) => getTileTargetKey(item) === getTileTargetKey(target))) return
    orderedTargets.push(target)
  }

  if (activeIndex >= 0) {
    addTarget(normalizedOptions[activeIndex + 1])
    addTarget(normalizedOptions[activeIndex - 1])
    addTarget(normalizedOptions[0])
    addTarget(normalizedOptions[activeIndex + 2])
    addTarget(normalizedOptions[activeIndex - 2])
  }

  normalizedOptions.forEach(addTarget)

  return orderedTargets.slice(0, limit)
}

export function getWellnessTilePrefetchTargetsFromData(
  data,
  { limit = NAV_PREFETCH_LIMIT, excludeTileId } = {},
) {
  const { tilesList } = getTilesData(data)
  const targets = []
  const seenTargets = new Set()

  if (!Array.isArray(tilesList)) {
    return targets
  }

  tilesList.forEach((tile) => {
    if (!Array.isArray(tile?.items)) return

    tile.items.forEach((card) => {
      const tabAndTile = card?.tabAndTile
      const normalizedTarget = normalizeWellnessPrefetchTarget(tabAndTile)

      if (!isPrefetchableTarget(normalizedTarget)) return
      if (normalizedTarget.tileId === excludeTileId) return
      if (!tabAndTile?.curatable && normalizedTarget.tileId !== 'practice') return

      const targetKey = getTileTargetKey(normalizedTarget)
      if (seenTargets.has(targetKey)) return

      seenTargets.add(targetKey)
      targets.push(normalizedTarget)
    })
  })

  return targets.slice(0, limit)
}

export function warmWellnessTileAssets(data, { maxImages = 3, priority = 'low' } = {}) {
  if (!data || typeof document === 'undefined') return

  const { preSelectedBackground, tilesList } = getTilesData(data)
  const imageUrls = []
  const addImageUrl = (imageId) => {
    const imageUrl = buildMediaUrl(imageId)
    if (imageUrl && !imageUrls.includes(imageUrl)) {
      imageUrls.push(imageUrl)
    }
  }

  addImageUrl(preSelectedBackground?.imageId)

  if (Array.isArray(tilesList)) {
    const heroCards =
      tilesList[0]?.categoryType === HERO_CARD
        ? tilesList[0]?.items?.filter((card) => card?.type === 'course') || []
        : []

    heroCards.slice(0, 1).forEach((card) => {
      const { heroCardLandscapeImage, heroCardPortraitImage } = getCourseCardDetails(card)
      addImageUrl(heroCardLandscapeImage)
      addImageUrl(heroCardPortraitImage)
    })

    tilesList.forEach((tile) => {
      if (!Array.isArray(tile?.items)) return

      tile.items.forEach((card) => {
        if (imageUrls.length >= maxImages) return

        if (card?.type === 'course') {
          addImageUrl(getCourseCardDetails(card).verticalIconImg)
        }

        addImageUrl(card?.tabAndTile?.image)
      })
    })
  }

  imageUrls.slice(0, maxImages).forEach((imageUrl) => {
    safePreloadImage(imageUrl, {
      as: 'image',
      fetchPriority: priority,
    })
  })
}

export function useWellnessTileData(tileId, policyId = '') {
  const [state, setState] = useState(() => {
    const cachedData = tileId ? getCachedWellnessTileData({ tileId, policyId }) : null

    return {
      loading: Boolean(tileId && !cachedData),
      data: cachedData,
      error: null,
    }
  })

  useEffect(() => {
    if (!tileId) {
      setState({ loading: false, data: null, error: null })
      return undefined
    }

    const cachedData = getCachedWellnessTileData({ tileId, policyId })
    if (cachedData) {
      setState({ loading: false, data: cachedData, error: null })
      return undefined
    }

    const controller =
      typeof window !== 'undefined' && window.AbortController
        ? new window.AbortController()
        : null
    setState({ loading: true, data: null, error: null })

    fetchWellnessTileData({ tileId, policyId }, { signal: controller?.signal })
      .then((data) => {
        if (!controller?.signal?.aborted) {
          setState({ loading: false, data, error: null })
        }
      })
      .catch((error) => {
        if (controller?.signal?.aborted || isCancelableError(error)) return
        setState({ loading: false, data: null, error })
      })

    return () => controller?.abort()
  }, [tileId, policyId])

  return state
}

export function useWellnessNavTilePrefetch(options = [], activeValue) {
  useEffect(() => {
    const candidates = getWellnessNavPrefetchCandidates(options, activeValue)
    const cleanups = candidates.map((candidate, index) =>
      scheduleWellnessTilePrefetch(candidate, {
        delay: 600 + index * 800,
        maxImages: 2,
        reason: 'nav-adjacent',
      }),
    )

    return () => cleanups.forEach((cleanup) => cleanup?.())
  }, [activeValue, options])
}
